import { useCallback, useState } from 'react';
import {
  CancelAdminChangelogEditionAPI,
  ScheduleAdminChangelogEditionAPI,
  SendAdminChangelogEditionNowAPI,
} from '../../../../api/AdminAPI';

const CLOSED_SCHEDULE = { edition: null, warnings: [], error: null };

/**
 * useEditionPublishing
 *
 * Agendar (com a confirmação dos avisos devolvidos pela API), desagendar, enviar agora e
 * cancelar um envio. Cada ação concluída chama onDone para recarregar a lista.
 *
 * @param {{ notify: Function, onDone: Function }} options
 */
function useEditionPublishing({ notify, onDone }) {
  const [schedule, setSchedule] = useState(CLOSED_SCHEDULE);
  const [sendNowEdition, setSendNowEdition] = useState(null);
  const [cancelEdition, setCancelEdition] = useState(null);
  const [busy, setBusy] = useState(null);

  const finish = useCallback((message) => {
    notify(message, 'success');
    if (onDone) onDone();
  }, [notify, onDone]);

  // ── Agendar ──
  const askSchedule = useCallback((edition) => setSchedule({ ...CLOSED_SCHEDULE, edition }), []);
  const closeSchedule = useCallback(() => { if (!busy) setSchedule(CLOSED_SCHEDULE); }, [busy]);

  /** @param {string} scheduledAt - "AAAA-MM-DD HH:MM" */
  const submitSchedule = useCallback((scheduledAt, confirm = false) => {
    const edition = schedule.edition;
    if (!edition) return;
    setBusy('schedule');
    ScheduleAdminChangelogEditionAPI(edition.id, scheduledAt, confirm, (response) => {
      setBusy(null);
      if (response?.needs_confirm) {
        setSchedule((prev) => ({ ...prev, warnings: response.warnings || [], error: null }));
        return;
      }
      if (!response || response.success === false) {
        setSchedule((prev) => ({ ...prev, error: response?.error || 'Não foi possível agendar o envio.' }));
        return;
      }
      setSchedule(CLOSED_SCHEDULE);
      finish('Envio agendado.');
    });
  }, [schedule.edition, finish]);

  const unschedule = useCallback((edition) => {
    setBusy('unschedule');
    ScheduleAdminChangelogEditionAPI(edition.id, null, true, (response) => {
      setBusy(null);
      if (!response || response.success === false) {
        notify(response?.error || 'Não foi possível desagendar o envio.', 'error');
        return;
      }
      finish('Envio voltou a rascunho.');
    });
  }, [notify, finish]);

  // ── Enviar agora ──
  const askSendNow = useCallback((edition) => setSendNowEdition(edition), []);
  const closeSendNow = useCallback(() => { if (!busy) setSendNowEdition(null); }, [busy]);

  const confirmSendNow = useCallback(() => {
    if (!sendNowEdition) return;
    setBusy('send-now');
    SendAdminChangelogEditionNowAPI(sendNowEdition.id, (response) => {
      setBusy(null);
      setSendNowEdition(null);
      if (!response || response.success === false) {
        notify(response?.error || 'Não foi possível enviar.', 'error');
        return;
      }
      const published = response.published_items || 0;
      finish(`Envio na fila para ${response.queued || 0} pessoa(s)${published ? `; ${published} novidade(s) publicada(s)` : ''}.`);
    });
  }, [sendNowEdition, notify, finish]);

  // ── Cancelar ──
  const askCancel = useCallback((edition) => setCancelEdition(edition), []);
  const closeCancel = useCallback(() => { if (!busy) setCancelEdition(null); }, [busy]);

  const confirmCancel = useCallback(() => {
    if (!cancelEdition) return;
    setBusy('cancel');
    CancelAdminChangelogEditionAPI(cancelEdition.id, (response) => {
      setBusy(null);
      setCancelEdition(null);
      if (!response || response.success === false) {
        notify(response?.error || 'Não foi possível cancelar o envio.', 'error');
        return;
      }
      finish('Envio cancelado.');
    });
  }, [cancelEdition, notify, finish]);

  return {
    busy,
    schedule, askSchedule, closeSchedule, submitSchedule, unschedule,
    sendNowEdition, askSendNow, closeSendNow, confirmSendNow,
    cancelEdition, askCancel, closeCancel, confirmCancel,
  };
}

export default useEditionPublishing;
